import Clickable from '@carvana/merch-components/Clickable';
import { useRef } from 'react';
import { Text } from '@/vdp/styles';
import { appAssetsCdnUrl } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useModalContext } from '~/context/vdp/ModalContext/ModalContext';
import type { WhyBuyNarrativeItem } from '~/hooks/vdp/selectors/useWhyBuyNarratives';
import optimizeImage from '~/utilities/optimizeImage';
import useWhyBuyCardVideo from './hooks/useWhyBuyCardVideo';
import StockImageBadge from './StockImageBadge';
import {
  Details,
  ImageryCard,
  ImageryCardContent,
  ImageryWrapper,
  StyledImage,
  StyledVideo
} from './WhyBuyWithImageryCard.styles';

type OwnProps = {
  bulletPoint: WhyBuyNarrativeItem;
  isActive: boolean;
  onActivate: () => void;
};

// TODO: replace with the video from the API (REO-9544)
const PLACEHOLDER_VIDEO_SRC = `${appAssetsCdnUrl}/videos/shipping.mp4`;

export default function WhyBuyWithImageryCard({
  bulletPoint,
  isActive,
  onActivate
}: OwnProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { setWhyBuyModalOpen } = useModalContext();
  const trackEvent = useTrackEvent();
  const { onError, onMouseEnter, onMouseLeave, videoRef } = useWhyBuyCardVideo({
    cardRef,
    isActive,
    isMobile,
    onActivate,
    src: PLACEHOLDER_VIDEO_SRC
  });

  // TODO: replace with stock image data from the API (REO-9544)
  const isStockImage = true;

  // TODO: replace with the media type from the API (REO-9544)
  const isVideo = true;

  // TODO: replace with the poster image from the API (REO-9544)
  const optimizedMobileImageUrl = optimizeImage(
    `${appAssetsCdnUrl}/images/shaqvana/shaqvana-hero-bg-mobile.jpg`,
    { format: 'auto', height: 135, optimize: 'high', quality: 90 }
  );

  const handleClick = () => {
    setWhyBuyModalOpen(true, { openTo: bulletPoint.id });
    trackEvent('Why Buy Feature', {
      description: 'why buy feature',
      item: bulletPoint.title
    });
  };

  return (
    <Clickable
      data-adobe-id={null}
      data-analytics-id={null}
      data-testid="why-buy-imagery-card"
      element={ImageryCard}
      elementRef={cardRef}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <ImageryWrapper>
        {isVideo ? (
          <StyledVideo
            data-testid="why-buy-imagery-video"
            draggable={false}
            muted
            onError={onError}
            playsInline
            poster={optimizedMobileImageUrl}
            preload="metadata"
            ref={videoRef}
            src={PLACEHOLDER_VIDEO_SRC}
          />
        ) : (
          <StyledImage
            alt="placeholder image"
            data-testid="why-buy-imagery-image"
            draggable={false}
            loading="lazy"
            src={optimizedMobileImageUrl}
          />
        )}
        {isStockImage && <StockImageBadge />}
      </ImageryWrapper>
      <ImageryCardContent>
        <div className="line-clamp-4">
          <h3 className="mb-lg text-text-strong t-heading-sm">
            {bulletPoint.title}
          </h3>
          <Text>{bulletPoint.content}</Text>
        </div>
        <Details data-testid="why-buy-details">Details</Details>
      </ImageryCardContent>
    </Clickable>
  );
}
